import React from 'react';
import { EnhancedEncryption, VpnKey, VisibilityOff, Security } from '@mui/icons-material';

const SecurityFeatures = () => {
    const features = [
        {
            icon: EnhancedEncryption,
            title: 'Chiffrement AES-256',
            description: 'Toutes les données sensibles sont chiffrées avec l\'algorithme AES-256, standard militaire.',
            details: ['Chiffrement en transit TLS 1.3', 'Chiffrement au repos', 'Clés de chiffrement managées']
        },
        {
            icon: VpnKey,
            title: 'Gestion Sécurisée des Clés',
            description: 'Système de gestion des clés avec rotation automatique et stockage HSM.',
            details: ['Rotation automatique des clés', 'Stockage HSM FIPS 140-2']
        },
        {
            icon: VisibilityOff,
            title: 'Masquage des Données',
            description: 'Masquage systématique des données sensibles dans les logs et interfaces.',
            details: ['Masquage des numéros de carte', 'Tokenisation des données', 'Pseudo-anonymisation']
        },
        {
            icon: Security,
            title: 'Mises à Jour de Sécurité',
            description: 'Mises à jour automatiques et tests de pénétration réguliers.',
            details: ['Correctifs automatiques', 'Tests de pénétration trimestriels', 'Audits de sécurité']
        }
    ];

    return (
        <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center px-6 py-3 rounded-full bg-amber-100 border border-amber-200 mb-6">
                        <span className="w-3 h-3 bg-amber-500 rounded-full mr-3 animate-pulse"></span>
                        <span className="text-amber-800 font-semibold text-lg">SÉCURITÉ MAXIMALE</span>
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                        Sécurité{' '}
                        <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                            de niveau bancaire
                        </span>
                    </h2>
                    <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                        Des technologies de pointe et des protocoles robustes pour protéger chaque aspect de vos transactions financières.
                    </p>
                </div>

                {/* Features Grid */}
                <div className="grid md:grid-cols-2 gap-8 mb-16">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="group relative bg-white/80 backdrop-blur-md rounded-2xl p-8 border border-amber-100/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                        >
                            {/* Hover Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-yellow-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                            <div className="relative z-10">
                                <div className="flex items-start space-x-6">
                                    <div className="bg-gradient-to-r from-amber-500 to-yellow-500 w-16 h-16 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                        <feature.icon className="text-2xl text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors">
                                            {feature.title}
                                        </h3>
                                        <p className="text-gray-700 mb-4 leading-relaxed">
                                            {feature.description}
                                        </p>
                                        <ul className="space-y-3">
                                            {feature.details.map((detail, idx) => (
                                                <li key={idx} className="flex items-center text-gray-700">
                                                    <div className="w-2 h-2 bg-amber-500 rounded-full mr-3 flex-shrink-0"></div>
                                                    <span className="text-sm">{detail}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Corner Accent */}
                            <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                                <div className="absolute top-0 right-0 w-8 h-8 bg-gradient-to-bl from-amber-500/20 to-transparent rounded-bl-2xl"></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Security Metrics */}
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl shadow-xl p-8 text-white">
                    <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold mb-2">Indicateurs de Performance Sécurité</h3>
                        <p className="text-white/80">Des chiffres qui témoignent de notre engagement sécurité</p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            { value: '99.99%', label: 'Uptime Sécurisé', icon: '🛡️' },
                            { value: '24/7', label: 'Surveillance Continue', icon: '👁️' },
                            { value: '<50ms', label: 'Détection des Menaces', icon: '⚡' },
                            { value: '0', label: 'Incidents Majeurs', icon: '✅' }
                        ].map((metric, index) => (
                            <div key={index} className="text-center group">
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all">
                                    <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">{metric.icon}</div>
                                    <div className="text-3xl font-bold mb-2">{metric.value}</div>
                                    <div className="text-white/80 text-sm">{metric.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Compliance Badges */}
                <div className="text-center mt-12">
                    <h4 className="text-lg font-semibold text-gray-700 mb-6">Certifications et Conformités</h4>
                    <div className="flex flex-wrap justify-center gap-6">
                        {[
                            { name: 'PCI DSS Level 1', color: 'from-green-500 to-emerald-500' },
                            { name: 'ISO 27001', color: 'from-blue-500 to-cyan-500' },
                            { name: 'SOC 2 Type II', color: 'from-purple-500 to-pink-500' },
                            { name: 'GDPR Compliant', color: 'from-indigo-500 to-blue-500' }
                        ].map((badge, index) => (
                            <div
                                key={index}
                                className={`bg-gradient-to-r ${badge.color} text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:scale-105 transition-transform`}
                            >
                                {badge.name}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SecurityFeatures;